import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { styled } from '@linaria/react';
import { COLORS } from '@livecommerce/design-system';
import { m } from 'framer-motion';
import useMeasure from 'react-use/lib/useMeasure.js';

import BillingAlert from './components-v3/BillingAlert/index.jsx';
import _Sidebar from './Sidebar.jsx';
import _Topbar from './Topbar.jsx';
import { useTopbarScrollAway } from './hooks/index.js';
import { mainTransitionConfig } from './constants/animationConfig.js';

import { useNavigationFrameContext } from 'contexts/NavigationFrameContext.jsx';
import useBillingAlert from 'hooks/useBillingAlert.js';

const Sidebar = styled(_Sidebar)`
  display: ${(props) => (props.isSidebarShown ? 'flex' : 'none')};
  grid-column: 1 / 2;
  grid-row: ${(props) => (props.isAlertShown ? '2' : '1')} / 4;
`;

const Topbar = styled(_Topbar)`
  display: ${(props) => (props.isTopbarShown ? 'grid' : 'none')};
  grid-column: ${(props) => (props.isSidebarShown ? '2' : '1')} / 3;
  grid-row: ${(props) => (props.isAlertShown ? '2 / 3' : '1 / 2')};
  z-index: 1;
  transform: translateY(${(props) => (props.isTopbarAnimatedOut || !props.isTopbarShown ? '0' : '-64px')});
  transition: transform 0.2s ease-in-out;
`;

const getPaddings = ({ isTopbarShown, contentHasPaddings }) => {
  if (!isTopbarShown && !contentHasPaddings) return '0';
  if (isTopbarShown && !contentHasPaddings) return '0px 0px 64px';
  if (!isTopbarShown && contentHasPaddings) return '40px';

  return '40px 40px 104px';
};

const Content = styled.div`
  position: relative;
  grid-column: ${(props) => (props.isSidebarShown ? '2' : '1')} / 3;
  grid-row: ${(props) => props.contentGridRow};
  background-color: white;
  border-top-left-radius: 12px;
  overflow: auto;
  padding: ${(props) => getPaddings(props)};
  transform: translateY(${(props) => (props.isTopbarAnimatedOut || !props.isTopbarShown ? '0' : '-64px')});
  transition: transform 0.2s ease-in-out;
  height: ${(props) => (props.isTopbarShown ? 'calc(100% + 64px)' : '100%')};
`;

const Container = styled.div`
  display: grid;
  grid-template-rows: ${(props) => (props.isAlertShown ? '72px' : '64px')} 64px 1fr;
  height: 100vh;
  background-color: ${COLORS.greyMediumLight};
  overflow: hidden;
`;

const AnimatedContainer = m(Container);

const SIDEBAR_COLLAPSE_WIDTH = 1450;

const getContentGridRow = (isAlertShown, isTopbarShown) => {
  if (isTopbarShown && isAlertShown) return '3 / 3';
  if (!isTopbarShown && !isAlertShown) return '1 / 4';
  return '2 / 4';
};

const Navigation = ({ children, ...props }) => {
  const {
    isTopbarShown,
    isSidebarShown,
    contentHasPaddings,
    isSidebarCollapsed,
    setIsSidebarCollapsed,
    setPreferSidebarCollapsed,
  } = useNavigationFrameContext();
  const [ref, { width }] = useMeasure();
  const { isAlertShown, isDeactivated } = useBillingAlert();

  useEffect(() => {
    if (width && width < SIDEBAR_COLLAPSE_WIDTH) {
      setPreferSidebarCollapsed(true);
    }
    if (width > SIDEBAR_COLLAPSE_WIDTH) {
      setPreferSidebarCollapsed(false);
    }
  }, [isSidebarCollapsed, setPreferSidebarCollapsed, width]);

  const { contentRef, isTopbarAnimatedOut } = useTopbarScrollAway();

  const contentGridRow = getContentGridRow(isAlertShown, isTopbarShown);

  return (
    <AnimatedContainer
      isAlertShown={isAlertShown}
      transition={mainTransitionConfig}
      initial={false}
      animate={{ gridTemplateColumns: isSidebarCollapsed ? '64px 1fr' : '250px 1fr' }}
      {...props}
      ref={ref}
    >
      <Sidebar
        collapsed={isSidebarCollapsed}
        setIsSidebarCollapsed={setIsSidebarCollapsed}
        isSidebarShown={isSidebarShown}
        isAlertShown={isAlertShown}
      />
      {isAlertShown && <BillingAlert isDeactivated={isDeactivated} />}
      <Topbar
        data-testid="navigation-topbar"
        isTopbarShown={isTopbarShown}
        isSidebarShown={isSidebarShown}
        isAlertShown={isAlertShown}
        isTopbarAnimatedOut={isTopbarAnimatedOut}
      />
      <Content
        ref={contentRef}
        transform={isTopbarAnimatedOut ? 'translateY(0)' : 'translateY(-64px)'}
        isTopbarAnimatedOut={isTopbarAnimatedOut}
        isTopbarShown={isTopbarShown}
        isSidebarShown={isSidebarShown}
        contentHasPaddings={contentHasPaddings}
        data-testid="newLayoutContent"
        contentGridRow={contentGridRow}
      >
        {children}
      </Content>
    </AnimatedContainer>
  );
};

Navigation.propTypes = {
  children: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.node), PropTypes.node]).isRequired,
};

export default Navigation;
